import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { marketplaceService } from '../../services/marketplaceService';
import { messagingService } from '../../services/messagingService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const AVATAR_FALLBACK = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='20' fill='%23e5e7eb'/%3E%3Ccircle cx='20' cy='15' r='7' fill='%239ca3af'/%3E%3Cellipse cx='20' cy='35' rx='12' ry='9' fill='%239ca3af'/%3E%3C/svg%3E";

const MarketplaceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [message, setMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    fetchItemDetails();
  }, [id]);

  const fetchItemDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await marketplaceService?.getItemById(id);
      
      if (!data) {
        setError('İlan bulunamadı');
        return;
      }

      setItem(data);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching item details:', err);
      setError(err?.message || 'İlan yüklenirken bir hata oluştu');
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!message?.trim()) {
      alert('Lütfen bir mesaj yazın');
      return;
    }

    try {
      setIsSendingMessage(true);

      // Use the new Direct Message flow
      await messagingService?.sendDirectMessage(
        item?.seller_id,
        message?.trim()
      );

      alert('Mesajınız gönderildi! Mesajlar sayfasından takip edebilirsiniz.');
      setShowMessageModal(false);
      setMessage('');
      // Redirect to messaging center to see the new chat
      navigate('/messaging-center');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Mesaj gönderilirken bir hata oluştu: ' + err?.message);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!window.confirm('Bu ilanı silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await marketplaceService?.deleteItem(item?.id);
      alert('İlan silindi');
      navigate('/user-marketplace');
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('İlan silinirken bir hata oluştu: ' + err?.message);
    }
  };

  const handleMarkAsSold = async () => {
    if (!window.confirm('Bu ilanı satıldı olarak işaretlemek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await marketplaceService?.markAsSold(item?.id);
      alert('İlan satıldı olarak işaretlendi');
      fetchItemDetails();
    } catch (err) {
      console.error('Error marking as sold:', err);
      alert('İlan güncellenirken bir hata oluştu: ' + err?.message);
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>İlan Detayları - PaddleTurkiye</title>
        </Helmet>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="flex items-center justify-center min-h-[50vh]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">İlan yükleniyor...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error State
  if (error || !item) {
    return (
      <>
        <Helmet>
          <title>İlan Bulunamadı - PaddleTurkiye</title>
        </Helmet>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="flex items-center justify-center min-h-[50vh]">
              <div className="text-center max-w-md">
                <div className="bg-red-100 p-4 rounded-full inline-flex mb-4">
                  <Icon name="AlertTriangle" size={48} className="text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">İlan Bulunamadı</h2>
                <p className="text-muted-foreground mb-6">
                  {error || 'Aradığınız ilan mevcut değil veya kaldırılmış olabilir.'}
                </p>
                <Link to="/user-marketplace">
                  <Button>
                    <Icon name="ArrowLeft" size={18} />
                    Pazaryerine Dön
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const isOwner = user?.id === item?.seller_id;

  // Resolve seller avatar URL the same way product images are resolved
  const resolveAvatarUrl = (avatarUrl) => {
    if (!avatarUrl) return null;
    if (avatarUrl?.startsWith('http://') || avatarUrl?.startsWith('https://')) return avatarUrl;
    const { data } = supabase?.storage?.from('profile-avatars')?.getPublicUrl(avatarUrl);
    return data?.publicUrl || null;
  };
  const sellerAvatarUrl = resolveAvatarUrl(item?.seller?.avatar);

  return (
    <>
      <Helmet>
        <title>{item?.title} - PaddleTurkiye Pazaryeri</title>
        <meta name="description" content={item?.description} />
      </Helmet>
      <Header />
      <div className="main-content bg-background min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-primary">Ana Sayfa</Link>
            <Icon name="ChevronRight" size={16} />
            <Link to="/user-marketplace" className="hover:text-primary">Pazaryeri</Link>
            <Icon name="ChevronRight" size={16} />
            <span className="text-foreground">{item?.title}</span>
          </nav>

          {/* Back to My Listings Button (only for owner) */}
          {isOwner && (
            <div className="mb-6">
              <Button
                onClick={() => navigate('/user-profile#ilanlarim')}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Icon name="ArrowLeft" size={18} />
                İlanlarıma Dön
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Images */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-xl shadow-sm overflow-hidden mb-6">
                {/* Main Image */}
                <div className="relative aspect-video bg-muted">
                  <img
                    src={item?.images?.[selectedImageIndex] || item?.image}
                    alt={item?.imageAlt}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/no_image.png'; }}
                  />
                  {item?.is_sold && (
                    <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg font-semibold">
                      SATILDI
                    </div>
                  )}
                </div>

                {/* Image Thumbnails */}
                {item?.images?.length > 1 && (
                  <div className="p-4 grid grid-cols-5 gap-2">
                    {item?.images?.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          selectedImageIndex === idx
                            ? 'border-primary ring-2 ring-primary ring-offset-2' :'border-border hover:border-primary'
                        }`}
                      >
                        <img src={img} alt={`Görsel ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/no_image.png'; }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="bg-card rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-foreground mb-4">Açıklama</h2>
                <p className="text-muted-foreground whitespace-pre-wrap">{item?.description}</p>
              </div>
            </div>

            {/* Right Column: Item Info & Seller */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-xl shadow-sm p-6 mb-6 sticky top-6">
                {/* Price */}
                <div className="mb-6">
                  <p className="text-sm text-muted-foreground mb-1">Fiyat</p>
                  <p className="text-3xl font-bold text-primary">
                    ₺{item?.price?.toLocaleString('tr-TR')}
                  </p>
                </div>

                {/* Item Details */}
                <div className="space-y-4 mb-6 pb-6 border-b border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Durum</span>
                    <span className="text-sm font-medium text-foreground capitalize">
                      {item?.condition === 'new' && 'Sıfır'}
                      {item?.condition === 'like-new' && 'Sıfır Gibi'}
                      {item?.condition === 'good' && 'İyi'}
                      {item?.condition === 'fair' && 'Orta'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Konum</span>
                    <span className="text-sm font-medium text-foreground">{item?.location}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">İlan Tarihi</span>
                    <span className="text-sm font-medium text-foreground">{item?.postedDate}</span>
                  </div>
                </div>

                {/* Seller Info */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Satıcı Bilgileri</h3>
                  <div className="flex items-center space-x-3 mb-4">
                    <img
                      src={sellerAvatarUrl || AVATAR_FALLBACK}
                      alt={item?.seller?.avatarAlt}
                      className="w-12 h-12 rounded-full object-cover"
                      onError={(e) => { e.target.onerror = null; e.target.src = AVATAR_FALLBACK; }}
                    />
                    <div>
                      <p className="font-medium text-foreground">{item?.seller?.name}</p>
                      {item?.seller?.location && (
                        <p className="text-sm text-muted-foreground flex items-center">
                          <Icon name="MapPin" size={14} className="mr-1" />
                          {item?.seller?.location}
                        </p>
                      )}
                    </div>
                  </div>
                  {item?.seller?.memberSince && (
                    <p className="text-xs text-muted-foreground">
                      Üyelik: {new Date(item?.seller?.memberSince)?.toLocaleDateString('tr-TR')}
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                {isOwner ? (
                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate(`/user-marketplace?edit=${item?.id}`)}
                    >
                      <Icon name="Edit" size={18} />
                      İlanı Düzenle
                    </Button>
                    {!item?.is_sold && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handleMarkAsSold}
                      >
                        <Icon name="Check" size={18} />
                        Satıldı Olarak İşaretle
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={handleDeleteItem}
                    >
                      <Icon name="Trash2" size={18} />
                      İlanı Sil
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="w-full"
                    onClick={() => setShowMessageModal(true)}
                    disabled={item?.is_sold}
                  >
                    <Icon name="MessageSquare" size={18} />
                    {item?.is_sold ? 'Ürün Satıldı' : 'Satıcıya Mesaj Gönder'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-foreground">Satıcıya Mesaj Gönder</h3>
              <button
                onClick={() => setShowMessageModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">
                <span className="font-medium text-foreground">{item?.seller?.name}</span> kullanıcısına mesaj gönderiyorsunuz
              </p>
              <p className="text-sm text-muted-foreground">
                İlan: <span className="font-medium text-foreground">{item?.title}</span>
              </p>
            </div>

            <textarea
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              placeholder="Mesajınızı buraya yazın..."
              className="w-full min-h-[120px] p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />

            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowMessageModal(false)}
              >
                İptal
              </Button>
              <Button
                className="flex-1"
                onClick={handleSendMessage}
                disabled={isSendingMessage || !message?.trim()}
              >
                {isSendingMessage ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplaceDetails;